import React, { useEffect, useState } from 'react'
import { useIncubationListing, useIncubationDetails } from './api'
import { FiDownload, FiEye } from 'react-icons/fi'
import { BsThreeDotsVertical } from 'react-icons/bs'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const DownloadIcon = FiDownload as React.ElementType
const EyeIcon = FiEye as React.ElementType
const MoreIcon = BsThreeDotsVertical as React.ElementType

interface IncubationItem {
  id: number
  startup_name: string
  legal_entity_type: string
  contact_name: string
  contact_email: string
  contact_phone: string
  industry_sector: string
  status: string
}

const tableFields: { label: string; key: keyof IncubationItem }[] = [
  { label: 'ID', key: 'id' },
  { label: 'Startup Name', key: 'startup_name' },
  { label: 'Entity Type', key: 'legal_entity_type' },
  { label: 'Contact Person', key: 'contact_name' },
  { label: 'Email', key: 'contact_email' },
  { label: 'Phone', key: 'contact_phone' },
  { label: 'Sector', key: 'industry_sector' },
  { label: 'Status', key: 'status' },
]

const IncubationDetail: React.FC<{ id: string; onBack: () => void }> = ({
  id,
  onBack,
}) => {
  const { data, isLoading, isError } = useIncubationDetails(id)
  const incubation = data?.data

  const fields = [
    { label: 'Startup Name', value: incubation?.startup_name },
    { label: 'Legal Entity Type', value: incubation?.legal_entity_type },
    { label: 'Contact Name', value: incubation?.contact_name },
    { label: 'Email', value: incubation?.contact_email },
    { label: 'Phone', value: incubation?.contact_phone },
    { label: 'Industry Sector', value: incubation?.industry_sector },
    { label: 'Status', value: incubation?.status },
  ]

  const downloadDetailPDF = () => {
    if (!incubation) return
    const doc = new jsPDF()
    doc.setFontSize(14)
    doc.text(`Incubation Detail - ID: ${incubation.id}`, 14, 20)

    const tableData: (string | number)[][] = fields.map((f) => {
      const value = f.value ?? 'N/A'
      return typeof value === 'string' || typeof value === 'number'
        ? [f.label, value]
        : [f.label, 'N/A']
    })

    autoTable(doc, {
      startY: 30,
      head: [['Field', 'Value']],
      body: tableData,
      styles: { fontSize: 10 },
    })

    doc.save(`Incubation_${incubation.id}.pdf`)
  }

  if (isLoading) return <p className="p-4">Loading...</p>
  if (isError || !incubation)
    return <p className="p-4 text-red-500">Failed to load details.</p>

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex justify-between items-center">
        <button onClick={onBack} className="text-blue-600 underline text-sm">
          ← Back to List
        </button>
        <button
          onClick={downloadDetailPDF}
          className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
        >
          Download PDF
        </button>
      </div>
      {fields.map((field, index) => (
        <div key={index} className="flex mb-4 text-sm">
          <div className="w-1/3 font-medium text-gray-700">{field.label}</div>
          <div className="w-2/3 text-gray-900">{field.value || 'N/A'}</div>
        </div>
      ))}
    </div>
  )
}
interface IncubationListingProps {
  onDataReady?: (data: IncubationItem[]) => void
  perPage?: number
  setPerPage?: (val: number) => void
}

const IncubationListing: React.FC<IncubationListingProps> = ({
  onDataReady,
  perPage,
  setPerPage,
}) => {
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [activeRowId, setActiveRowId] = useState<number | null>(null)

  const {
    data: response,
    isLoading,
    isError,
  } = useIncubationListing({
    page,
    page_size: perPage || 10,
  })
  const items: IncubationItem[] = response?.data || []
  const totalPages = response?.meta?.last_page || 1

  const downloadListPDF = () => {
    if (items.length === 0) return
    const doc = new jsPDF()
    doc.setFontSize(14)
    doc.text('Incubation List', 14, 20)

    const tableData: (string | number)[][] = items.map((item) =>
      tableFields.map((field) => {
        const value = item[field.key]
        return typeof value === 'string' || typeof value === 'number'
          ? value
          : 'N/A'
      })
    )

    autoTable(doc, {
      startY: 30,
      head: [tableFields.map((f) => f.label)],
      body: tableData,
      styles: { fontSize: 10 },
    })

    doc.save('Incubation_List.pdf')
  }
  useEffect(() => {
    if (onDataReady) onDataReady(items)
  }, [items, onDataReady])

  if (isLoading) return <div className="text-center mt-4">Loading...</div>
  if (isError)
    return (
      <div className="text-center mt-4 text-red-500">Error loading data.</div>
    )
  if (items.length === 0)
    return <div className="text-center mt-4 text-gray-500">No Data Found</div>

  if (selectedId) {
    return (
      <IncubationDetail
        id={String(selectedId)}
        onBack={() => setSelectedId(null)}
      />
    )
  }

  return (
    <div className="mt-6">
      <div className="flex justify-end mb-2">
        <button
          onClick={downloadListPDF}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg"
        >
          <DownloadIcon className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 rounded-md shadow-sm text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              {tableFields.map((field) => (
                <th
                  key={field.key}
                  className="px-4 py-2 border-b text-gray-700 font-semibold"
                >
                  {field.label}
                </th>
              ))}
              <th className="px-4 py-2 border-b text-gray-700 font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="odd:bg-white even:bg-gray-50">
                {tableFields.map((field) => (
                  <td key={field.key} className="px-4 py-2 border-b">
                    {item[field.key]}
                  </td>
                ))}
                <td className="px-4 py-2 border-b relative">
                  {activeRowId === item.id ? (
                    <EyeIcon
                      className="cursor-pointer text-blue-600"
                      onClick={() => setSelectedId(item.id)}
                    />
                  ) : (
                    <MoreIcon
                      className="cursor-pointer text-gray-600"
                      onClick={() => setActiveRowId(item.id)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center gap-4 mt-4 pr-4 px-4">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Per Page:</label>
          <select
            value={perPage || 10}
            onChange={(e) => {
              const size = Number(e.target.value)
              setPage(1)
              setPerPage?.(size)
            }}
            className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
          >
            {[5, 10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className={`px-3 py-1 border rounded ${page === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'}`}
          >
            Prev
          </button>
          <span className="text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
            className={`px-3 py-1 border rounded ${page === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'}`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

export default IncubationListing
